import React from 'react';

/**
 * ProfileTab – static summary & recommendations block.
 *
 * The Risk Assessment was extracted from this tab into its own
 * top-level sidebar entry (RiskAssessmentTab) on 2026-06-17 — the
 * assessment is substantial enough to warrant its own surface and the
 * student mental model treats "my risk profile" as distinct from
 * "my financial profile summary."
 *
 * Props
 *  ─ emergencyFund: { sixMonthGoal: number }
 */
const ProfileTab = ({
  emergencyFund,
}) => (
  <div className="space-y-6">
    {/* Original Financial Profile Summary */}
    <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-6">Financial Profile Summary</h3>

      {/* Two-column summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Income sources */}
        <div>
          <h4 className="font-medium mb-3 text-arc-muted">Income Sources</h4>
          <div className="space-y-2">
            {[
              ['Primary Salary', 'Active'],
              ['Business Revenue', 'Variable'],
              ['Rental Income', 'Passive'],
              ['Crypto Income', 'Variable'],
              ['Affiliate Income', 'Variable'],
            ].map(([label, status]) => (
              <div key={label} className="flex justify-between">
                <span>{label}</span>
                <span className="font-semibold">{status}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Health metrics */}
        <div>
          <h4 className="font-medium mb-3 text-arc-muted">Financial Health Metrics</h4>
          <div className="space-y-2">
            {[
              ['Debt-to-Income Ratio', 'Low', 'text-green-600'],
              ['Emergency Fund Status', 'Needs Attention', 'text-red-600'],
              ['Investment Diversification', 'Moderate', 'text-yellow-600'],
              ['FIRE Progress', 'On Track', 'text-blue-600'],
            ].map(([label, value, color]) => (
              <div key={label} className="flex justify-between">
                <span>{label}</span>
                <span className={`font-semibold ${color}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Habits — generic financial-wellness principles, not
          personalized advice. The previous version included
          "Consider rebalancing crypto-heavy investment portfolio"
          which crossed the advice line. Reframed as habits that any
          student can practice independent of their specific portfolio. */}
      <div className="mt-6 p-4 bg-white/[0.03] rounded-lg">
        <h4 className="font-medium mb-2">Healthy financial habits</h4>
        <ul className="text-sm space-y-1 text-arc-muted list-disc list-inside">
          <li>
            Build emergency fund to 6&nbsp;months of expenses ($
            {emergencyFund.sixMonthGoal.toLocaleString()})
          </li>
          <li>Pay down high-interest debt before adding new risk</li>
          <li>Track and categorize large expenses for budgeting clarity</li>
          <li>Diversify income streams where possible</li>
          <li>Revisit your Risk Profile when life circumstances change</li>
        </ul>
      </div>
    </div>

    {/* Risk Assessment used to render here. It now lives in its own
        top-level sidebar tab (see RiskAssessmentTab) so students find
        it faster and can return to it as a primary surface. */}
  </div>
);

export default ProfileTab;